import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import PersonIcon from "@mui/icons-material/Person";
import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import viewpaths from "global/constants/ViewPathConstants";
import {
  openErrorNotification,
  openSuccessNotification,
} from "helpers/methods";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { publishBlog, rejectBlog } from "screens/Admin/AdminDashboardServices";
import type { BlogDetailItem } from "screens/Blogs/BlogsServices";
import { getBlogById } from "screens/Blogs/BlogsServices";
import { formatDescriptionMarkdown } from "screens/Blogs/formatDescriptionMarkdown";
import fallbackImage from "../../../../assets/images/thumb-panacea-insights.jpg";
import blogEditPageStyles from "./BlogEditPage.styles";

const FALLBACK_IMAGE =
  typeof fallbackImage === "string"
    ? fallbackImage
    : ((fallbackImage as { default?: string })?.default ?? "");

const ACCEPTED_IMAGE_TYPES = [".jpg", ".jpeg", ".png", ".webp", ".gif"];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB

const formatDate = (dateString: string) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const TOPIC_LABELS: Record<string, string> = {
  hydroponics: "Hydroponics",
  technology: "Technology",
  innovation: "Innovation",
  wellness: "Wellness",
  finance: "Finance",
  philanthropy: "Philanthropy",
  entrepreneurship: "Entrepreneurship",
};
const getTopicLabel = (key: string) =>
  (key && TOPIC_LABELS[key.toLowerCase()]) || key || "—";

interface EditForm {
  title: string;
  summary: string;
  image: string;
  content: string;
}

const emptyForm: EditForm = {
  title: "",
  summary: "",
  image: "",
  content: "",
};

const BlogEditPage = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const theme = useTheme();
  const styles = blogEditPageStyles;
  const [blog, setBlog] = useState<BlogDetailItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [form, setForm] = useState<EditForm>(emptyForm);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await getBlogById(id);
      if (data) {
        setBlog(data);
        setForm({
          title: data.title ?? "",
          summary: data.description ?? "",
          image: data.image ?? "",
          content: data.content ?? "",
        });
      } else {
        setBlog(null);
      }
    } catch (e: any) {
      setBlog(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await publishBlog(id, {
        title: form.title,
        description: form.summary,
        content: form.content,
        image: form.image,
      });
      openSuccessNotification("Blog updated successfully.");
      history.push(viewpaths.adminBlogsViewPath);
    } catch (e: any) {
      openErrorNotification(e?.message ?? "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (!id) return;
    setRejecting(true);
    try {
      await rejectBlog(id);
      openSuccessNotification("Blog rejected.");
      history.push(viewpaths.adminBlogsViewPath);
    } catch (e: any) {
      openErrorNotification(e?.message ?? "Failed to reject.");
    } finally {
      setRejecting(false);
    }
  };

  const handleBack = () => {
    history.push(viewpaths.adminBlogsViewPath);
  };

  const handleImageFile = (file: File) => {
    const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!ACCEPTED_IMAGE_TYPES.includes(ext)) {
      openErrorNotification("Please use JPG, PNG, WebP or GIF.");
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      openErrorNotification("Image must be under 5MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setForm((f) => ({ ...f, image: reader.result as string }));
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file?.type.startsWith("image/")) handleImageFile(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleImageFile(file);
    e.target.value = "";
  };

  const handleRemoveImage = () => {
    setForm((f) => ({ ...f, image: "" }));
  };

  if (loading && !blog) {
    return (
      <Box sx={{ ...styles.root, pt: 12 }}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={48} sx={{ color: "#f8481c" }} />
        </Box>
      </Box>
    );
  }

  if (!id || (!loading && !blog)) {
    return (
      <Box sx={{ ...styles.root, pt: 12 }}>
        <Typography color="error">Blog not found.</Typography>
        <Button
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Back to Generated Blogs
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={styles.root}>
      <Box sx={styles.backgroundLayer}>
        <Box sx={styles.gradientOverlay} />
      </Box>

      <Container maxWidth="lg" sx={styles.contentWrapper}>
        <Box sx={styles.headerRow}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={styles.backButton}
          >
            Back to Generated Blogs
          </Button>
          <Box sx={styles.headerTitleBlock}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Box sx={styles.headerBox}>
                <Typography sx={styles.headerText}>Edit Blog</Typography>
                <Typography sx={styles.subHeaderText}>
                  Update your blog content and see the preview live before
                  publishing.
                </Typography>
              </Box>
            </motion.div>
          </Box>
        </Box>

        <Grid container spacing={{ xs: 3, md: 4 }} sx={styles.twoColGrid}>
          <Grid item xs={12}>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              style={{ height: "100%", minHeight: 0 }}
            >
              <Box sx={styles.sectionsGrid}>
                {/* Row 1 left: Image → Title → Summary */}
                <Box sx={styles.sectionCard}>
                  <Typography sx={styles.sectionTitle}>
                    Featured Image, Title & Summary
                  </Typography>
                  <Box sx={styles.sectionCardScroll}>
                    {/* Image – Dropzone & Preview */}
                    <Box sx={{ mb: 2 }}>
                      {form.image ? (
                        <Box sx={styles.imagePreview}>
                          <img src={form.image} alt="Featured" />
                          <Button
                            size="small"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={handleRemoveImage}
                            sx={styles.imageRemoveBtn}
                          >
                            Remove
                          </Button>
                        </Box>
                      ) : (
                        <>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            style={{ display: "none" }}
                            onChange={handleFileInputChange}
                          />
                          <Box
                            sx={{
                              borderRadius: "12px",
                              border: "2px dashed",
                              borderColor: dragOver
                                ? alpha(theme.palette.primary.main, 0.8)
                                : alpha(theme.palette.divider, 0.4),
                              bgcolor: dragOver
                                ? alpha(theme.palette.primary.main, 0.08)
                                : theme.palette.mode === "dark"
                                  ? alpha(theme.palette.background.default, 0.3)
                                  : alpha(theme.palette.grey[50], 0.8),
                              py: 4,
                              px: 2,
                              textAlign: "center",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                borderColor: alpha(
                                  theme.palette.primary.main,
                                  0.6,
                                ),
                                bgcolor: alpha(
                                  theme.palette.primary.main,
                                  0.04,
                                ),
                              },
                            }}
                            onDragOver={(e: React.DragEvent) => {
                              e.preventDefault();
                              setDragOver(true);
                            }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <CloudUploadIcon
                              sx={{
                                fontSize: 48,
                                color: alpha(theme.palette.text.secondary, 0.6),
                                mb: 1,
                              }}
                            />
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontWeight: 600, mb: 0.5 }}
                            >
                              Drag and drop an image here, or click to browse
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              JPG, PNG, WebP or GIF · Max 5MB
                            </Typography>
                          </Box>
                        </>
                      )}
                    </Box>

                    {/* Title */}
                    <Box sx={{ mb: 2 }}>
                      <Typography component="label" sx={styles.fieldLabel}>
                        Title
                      </Typography>
                      <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Enter blog title"
                        value={form.title}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, title: e.target.value }))
                        }
                        sx={styles.textField}
                        InputProps={{
                          sx: {
                            fontSize: "1rem",
                            fontWeight: 500,
                          },
                        }}
                      />
                    </Box>

                    {/* Summary */}
                    <Box sx={{ mb: 0 }}>
                      <Typography component="label" sx={styles.fieldLabel}>
                        Summary
                      </Typography>
                      <TextField
                        fullWidth
                        variant="outlined"
                        multiline
                        minRows={3}
                        maxRows={8}
                        placeholder="Brief summary or excerpt for the blog"
                        value={form.summary}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, summary: e.target.value }))
                        }
                        sx={styles.textField}
                        InputProps={{
                          sx: {
                            fontSize: "0.9375rem",
                            alignItems: "flex-start",
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Row 1 right: As on Blogs page */}
                <Box sx={styles.sectionCard}>
                  <Typography sx={styles.sectionTitle}>
                    As on Blogs page
                  </Typography>
                  <Box sx={styles.sectionCardScroll}>
                    <Box sx={styles.blogCardPreview}>
                      <Box sx={styles.blogCardPreviewImageWrap}>
                        <Box
                          component="img"
                          src={form.image || FALLBACK_IMAGE}
                          alt={form.title || "Blog"}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            if (target && FALLBACK_IMAGE)
                              target.src = FALLBACK_IMAGE;
                          }}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: `linear-gradient(180deg, transparent 0%, ${alpha("#f8481c", 0.2)} 100%)`,
                            opacity: 0.6,
                          }}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            top: 12,
                            left: 12,
                            px: 1.5,
                            py: 0.75,
                            background: alpha("#f8481c", 0.95),
                            borderRadius: 0,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "11px",
                              fontWeight: 700,
                              color: "#fff",
                              letterSpacing: "0.5px",
                            }}
                          >
                            {getTopicLabel(blog?.topic ?? "")}
                          </Typography>
                        </Box>
                      </Box>
                      <Stack spacing={1.5} sx={styles.blogCardPreviewBody}>
                        <Typography
                          component="h2"
                          sx={{
                            fontSize: "1.1rem",
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                            lineHeight: 1.3,
                            "& .blog-markdown-p": {
                              margin: 0,
                              fontSize: "inherit",
                            },
                          }}
                          dangerouslySetInnerHTML={{
                            __html: formatDescriptionMarkdown(
                              form.title || "Blog title",
                            ),
                          }}
                        />
                        <Stack
                          direction="row"
                          spacing={2}
                          flexWrap="wrap"
                          sx={{
                            pb: 1,
                            borderBottom: `1px solid ${alpha(
                              theme.palette.divider,
                              0.2,
                            )}`,
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={0.75}
                            alignItems="center"
                          >
                            <CalendarTodayIcon
                              sx={{
                                fontSize: "14px",
                                color: "#f8481c",
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: "0.8125rem",
                                color: theme.palette.text.secondary,
                                fontWeight: 500,
                              }}
                            >
                              {formatDate(blog?.publishDate ?? "")}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            spacing={0.75}
                            alignItems="center"
                          >
                            <PersonIcon
                              sx={{
                                fontSize: "14px",
                                color: "#f8481c",
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: "0.8125rem",
                                color: theme.palette.text.secondary,
                                fontWeight: 500,
                              }}
                            >
                              {blog?.author ?? "—"}
                            </Typography>
                          </Stack>
                        </Stack>
                        <Box
                          sx={{
                            fontSize: "0.875rem",
                            color: theme.palette.text.secondary,
                            lineHeight: 1.6,
                            "& .blog-markdown-p": {
                              margin: 0,
                              fontSize: "inherit",
                            },
                          }}
                          dangerouslySetInnerHTML={{
                            __html: formatDescriptionMarkdown(
                              form.summary || "Summary will appear here.",
                            ),
                          }}
                        />
                      </Stack>
                    </Box>
                  </Box>
                </Box>

                {/* Row 2 left: Content (Markdown) */}
                <Box sx={styles.contentSectionForm}>
                  <Typography sx={styles.sectionTitle}>
                    Content (Markdown)
                  </Typography>
                  <Box sx={styles.sectionCardScroll}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      multiline
                      minRows={10}
                      maxRows={20000}
                      placeholder="e.g. # Heading, **bold**, - list item, > blockquote..."
                      value={form.content}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, content: e.target.value }))
                      }
                      sx={styles.textField}
                      InputProps={{
                        sx: {
                          fontSize: "0.9375rem",
                          fontFamily: "monospace",
                          alignItems: "flex-start",
                        },
                      }}
                    />
                  </Box>
                </Box>

                {/* Row 2 right: As on Blog detail page (content) */}
                <Box sx={styles.contentSectionPreview}>
                  <Typography sx={styles.sectionTitle}>
                    As on Blog detail page (content)
                  </Typography>
                  <Box sx={styles.sectionCardScroll}>
                    <style>
                      {`
              .blog-edit-preview.blog-content {
                line-height: 1.9;
                color: ${theme.palette.text.primary};
              }
              .blog-edit-preview.blog-content .blog-markdown-h1,
              .blog-edit-preview.blog-content h1 {
                font-size: 1.75rem;
                font-weight: 700;
                margin: 0px;
                color: ${theme.palette.text.primary};
                line-height: 1.3;
              }
              .blog-edit-preview.blog-content .blog-markdown-h2,
              .blog-edit-preview.blog-content h2 {
                font-size: 1.4rem;
                font-weight: 700;
                margin: 0px;
                color: ${theme.palette.text.primary};
                line-height: 1.3;
              }
              .blog-edit-preview.blog-content .blog-markdown-h3,
              .blog-edit-preview.blog-content h3 {
                font-size: 1.2rem;
                font-weight: 700;
                margin: 0px;
                color: ${theme.palette.text.primary};
                line-height: 1.3;
              }
              .blog-edit-preview.blog-content .blog-markdown-h4,
              .blog-edit-preview.blog-content .blog-markdown-h5,
              .blog-edit-preview.blog-content .blog-markdown-h6,
              .blog-edit-preview.blog-content h4,
              .blog-edit-preview.blog-content h5,
              .blog-edit-preview.blog-content h6 {
                font-weight: 700;
                margin: 0px;
                color: ${theme.palette.text.primary};
                line-height: 1.3;
              }
              .blog-edit-preview.blog-content h4, .blog-edit-preview.blog-content .blog-markdown-h4 { font-size: 1.1rem; }
              .blog-edit-preview.blog-content h5, .blog-edit-preview.blog-content .blog-markdown-h5 { font-size: 1rem; }
              .blog-edit-preview.blog-content h6, .blog-edit-preview.blog-content .blog-markdown-h6 { font-size: 0.95rem; }
              .blog-edit-preview.blog-content .blog-markdown-p,
              .blog-edit-preview.blog-content p {
                margin: 0px;
                font-size: 17px;
                line-height: 1.9;
                color: ${theme.palette.text.secondary};
              }
              .blog-edit-preview.blog-content p:first-of-type {
                font-size: 19px;
                font-weight: 400;
                color: ${theme.palette.text.primary};
                line-height: 1.9;
              }
              .blog-edit-preview.blog-content .blog-markdown-blockquote,
              .blog-edit-preview.blog-content blockquote {
                margin: 0px;
                padding: 16px 20px;
                border-left: 4px solid #f8481c;
                background: ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)"};
                border-radius: 0 8px 8px 0;
                font-style: italic;
              }
              .blog-edit-preview.blog-content .blog-markdown-hr,
              .blog-edit-preview.blog-content hr {
                border: none;
                border-top: 1px solid ${theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.5) : alpha(theme.palette.common.black, 0.45)};
                margin: 0px;
              }
              .blog-edit-preview.blog-content .blog-markdown-ul,
              .blog-edit-preview.blog-content .blog-markdown-ol,
              .blog-edit-preview.blog-content ul,
              .blog-edit-preview.blog-content ol {
                margin: 0px;
                padding-left: 1.5rem;
              }
              .blog-edit-preview.blog-content .blog-markdown-ul li,
              .blog-edit-preview.blog-content .blog-markdown-ol li,
              .blog-edit-preview.blog-content ul li,
              .blog-edit-preview.blog-content ol li {
                margin: 0px;
                line-height: 1.7;
                color: ${theme.palette.text.secondary};
              }
              .blog-edit-preview.blog-content .blog-markdown-code,
              .blog-edit-preview.blog-content code {
                background: ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)"};
                padding: 2px 6px;
                border-radius: 4px;
                font-size: 0.9em;
              }
              .blog-edit-preview.blog-content .blog-markdown-link {
                color: #f8481c;
                text-decoration: underline;
              }
              .blog-edit-preview.blog-content .blog-markdown-link:hover {
                text-decoration: none;
              }
              .blog-edit-preview.blog-content .blog-markdown-img {
                max-width: 100%;
                height: auto;
                border-radius: 8px;
                margin: 12px 0;
              }
              .blog-edit-preview.blog-content .panacea-takeaway-header {
                position: relative;
                color: ${theme.palette.mode === "dark" ? "#ff6b4a" : "#c0392b"};
                font-weight: 800;
                font-size: 1.35rem;
                letter-spacing: 0.04em;
                text-transform: uppercase;
                padding: 16px 20px 16px 24px;
                margin: 0px;
                border: none;
                border-left: 5px solid #f8481c;
                border-radius: 0 12px 12px 0;
                background: linear-gradient(105deg, ${alpha("#f8481c", theme.palette.mode === "dark" ? 0.22 : 0.12)} 0%, ${alpha("#ff6b4a", theme.palette.mode === "dark" ? 0.08 : 0.05)} 50%, ${alpha(theme.palette.background.paper, 0.5)} 100%);
                box-shadow: 0 4px 20px ${alpha("#f8481c", theme.palette.mode === "dark" ? 0.2 : 0.12)};
              }
              .blog-edit-preview.blog-content .panacea-takeaway-header::before {
                content: "";
                position: absolute;
                top: 0;
                right: 0;
                width: 60px;
                height: 100%;
                background: linear-gradient(90deg, transparent 0%, ${alpha("#f8481c", 0.06)} 100%);
                border-radius: 0 12px 12px 0;
                pointer-events: none;
              }
              .blog-edit-preview.blog-content .panacea-takeaway-header::after {
                content: "◆";
                position: absolute;
                left: 8px;
                top: 50%;
                transform: translateY(-50%);
                font-size: 0.5rem;
                color: #f8481c;
                opacity: 0.8;
              }
              .blog-edit-preview.blog-content .panacea-takeaway-header {
                padding-left: 28px;
              }
            `}
                    </style>
                    <Box
                      className="blog-edit-preview blog-content"
                      sx={{
                        maxWidth: "100%",
                        p: 2.5,
                        borderRadius: "12px",
                        border: `1px solid ${alpha(theme.palette.divider, 0.25)}`,
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? alpha(theme.palette.background.default, 0.4)
                            : alpha(theme.palette.grey[50], 0.8),
                        minHeight: 280,
                      }}
                      dangerouslySetInnerHTML={{
                        __html: formatDescriptionMarkdown(form.content || ""),
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </motion.div>
          </Grid>
        </Grid>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
            mt: 2,
            mb: 4,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            startIcon={
              rejecting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <DoNotDisturbIcon />
              )
            }
            onClick={handleReject}
            disabled={rejecting || saving}
            sx={styles.rejectButton}
          >
            {rejecting ? "Rejecting…" : "Reject"}
          </Button>
          <Button
            variant="contained"
            startIcon={
              saving ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            onClick={handleSave}
            disabled={saving || rejecting}
            sx={styles.primaryButton}
          >
            {saving ? "Publishing..." : "Publish"}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default BlogEditPage;
